import { tv } from '~/utils/tv'

export const ChipsContainer = tv({
  base: `
    flex items-center justify-center rounded-pill min-width-size-huge p-px outline-none
    active:border-focused focus:border-focused w-fit m-px border-halfpulse border-transparent
    focus-visible:border-action-border-focused
  `,
  variants: {
    disabled: {
      true: 'cursor-not-allowed border-transparent',
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

export const ChipsVariants = tv({
  base: `
    group flex items-center justify-center rounded-pill min-w-16 relative px-fourpulse gap-twopulse border-quarterpulse text-text-neutral-alternative
    bg-action-fill-neutral-secondary-enabled border-action-border-neutral-secondary-enabled
    hover:border-action-border-neutral-secondary-hovered hover:bg-action-fill-neutral-secondary-hovered
    active:bg-action-fill-neutral-secondary-pressed active:border-action-border-neutral-secondary-pressed cursor:pointer
  `,
  variants: {
    size: {
      sm: '',
      md: 'h-medium',
      ml: 'h-mediumlarge',
    },
    readOnly: {
      true: `
          border-action-border-neutral-secondary-enabled border-quarterpulse bg-action-fill-neutral-secondary-enabled
          hover:bg-action-fill-neutral-secondary-enabled hover:border-action-border-neutral-secondary-enabled
          active:border-action-border-neutral-secondary-enabled active:bg-action-fill-neutral-secondary-enabled
        `,
    },
    disabled: {
      true: `
          cursor-not-allowed border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
          hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
          active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
        `,
    },
    checked: {
      true: `
          border-action-border-neutral-secondary-enabled border-halfpulse bg-action-fill-neutral-secondary-activated pl-twopulse text-text-neutral
          hover:text-text-neutral
        `,
    },
  },
  compoundVariants: [
    {
      disabled: true,
      checked: true,
      class: `
        cursor-not-allowed border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
        hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
        active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
      `,
    },
  ],
  defaultVariants: {
    size: 'md',
    disabled: false,
    checked: false,
  },
})

export const ChipsInputVariants = tv({
  base: `
    group flex items-center justify-center rounded-pill min-w-10 relative px-twopulse py-onepulse gap-onepulse border-quarterpulse text-text-neutral-alternative
    bg-action-fill-neutral-secondary-enabled border-action-border-neutral-secondary-enabled
    active:bg-action-fill-neutral-secondary-pressed active:border-action-border-neutral-secondary-pressed cursor:pointer
    hover:border-action-border-neutral-secondary-hovered hover:bg-action-fill-neutral-secondary-hovered
  `,
  variants: {
    size: {
      sm: 'h-small',
      md: 'h-medium',
      ml: 'h-medium',
    },
    disabled: {
      true: `
          cursor-not-allowed border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
          hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
          active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
        `,
    },
    readOnly: {
      true: `
          cursor-not-allowed text-text-neutral-readonly border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
          hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
          active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
        `,
    },
    checked: {
      true: 'border-action-border-neutral-secondary-enabled border-quarterpulse',
    },
  },
  compoundVariants: [
    {
      disabled: true,
      checked: true,
      class: `
        cursor-not-allowed border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
        hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
        active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
      `,
    },
    {
      checked: true,
      readOnly: true,
      class: `
        cursor-not-allowed text-text-neutral-readonly border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
        hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
        active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
      `,
    },
  ],
  defaultVariants: {
    size: 'md',
    disabled: false,
    checked: false,
  },
})

export const ChipsButtonVariants = tv({
  base: `
    group flex items-center justify-center rounded-pill min-w-[64px] relative px-twopulse gap-twopulse border-quarterpulse text-[length:var(--font-size-threeandhalfpulse)] text-text-neutral-alternative outline-none
    bg-action-fill-neutral-secondary-enabled border-action-border-neutral-secondary-enabled
    has-hover:bg-action-fill-neutral-secondary-hovered has-hover:border-action-border-neutral-secondary-hovered
    active:bg-action-fill-neutral-secondary-pressed active:border-action-border-neutral-secondary-pressed cursor:pointer
    focus-visible:outline-action-border-focused
  `,
  variants: {
    size: {
      sm: 'h-small',
      md: 'h-medium',
      ml: 'h-mediumlarge',
    },
    readOnly: {
      true: `
        cursor-not-allowed border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
        hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
        active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
      `,
    },
    disabled: {
      true: `
          cursor-not-allowed border-action-border-neutral-secondary-disabled border-quarterpulse bg-action-fill-neutral-secondary-disabled
          hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
          active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
        `,
    },
    count: {
      false: '',
      true: '',
    },
    hasChecked: {
      true: `
          border-action-border-neutral-secondary-activated border-halfpulse bg-action-fill-neutral-secondary-activated text-text-neutral
          has-hover:bg-action-fill-neutral-secondary-hovered has-hover:border-action-border-neutral-secondary-hovered
          active:bg-action-fill-neutral-secondary-hovered active:border-action-border-neutral-secondary-activated
      `,
    },
  },
  compoundVariants: [
    {
      disabled: true,
      hasChecked: true,
      class: `
        cursor-not-allowed border-action-border-neutral-secondary-disabled border-halfpulse bg-action-fill-neutral-secondary-disabled
        hover:bg-action-fill-neutral-secondary-disabled hover:border-action-border-neutral-secondary-disabled
        active:border-action-border-neutral-secondary-disabled active:bg-action-fill-neutral-secondary-disabled
      `,
    },
  ],
  defaultVariants: {
    size: 'md',
    disabled: false,
    checked: false,
  },
})

export const ChipsLabelVariants = tv({
  base: 'flex h-extrasmall items-center justify-center font-bold text-text-neutral-alternative leading-small',
  variants: {
    size: {
      sm: 'text-threeandhalfpulse',
      md: 'h-medium min-w-medium text-threeandhalfpulse',
      ml: 'h-mediumlarge min-w-mediumlarge text-threeandhalfpulse',
    },
    disabled: {
      true: 'cursor-not-allowed text-text-neutral-disabled',
    },
    readonly: {
      true: 'cursor-not-allowed text-text-neutral-readonly',
    },
    checked: {
      true: 'text-action-text-neutral',
    },
  },
  compoundVariants: [
    {
      checked: true,
      disabled: true,
      class: 'cursor-not-allowed text-text-neutral-disabled',
    },
  ],
  defaultVariants: {
    disabled: false,
    checked: false,
  },
})

export const RadioButtonVariants = tv({
  base: `
        hidden
      `,
})

export const ChipsIcoChecked = tv({
  base: `
       hidden group-has-[:checked]:block
      `,
  variants: {
    size: {
      sm: 'h-small',
      md: 'h-tiny',
      ml: 'h-small',
    },
    disabled: {
      true: 'cursor-not-allowed',
    },
  },
})

export const ChipsInputIcoChecked = tv({
  base: '',
  variants: {
    size: {
      sm: 'h-small',
      md: 'h-small',
      ml: 'h-small',
    },
    disabled: {
      true: 'cursor-not-allowed',
    },
  },
})

export const ChipsIcoMultiple = tv({
  variants: {
    size: {
      sm: 'h-small',
      md: 'h-extrasmall',
      ml: 'h-small',
    },
    disabled: {
      true: 'cursor-not-allowed',
    },
  },
})

export const ChipsBadage = tv({
  base: ` flex justify-center items-center rounded-pill border-none bg-text-neutral text-threepulse text-text-neutral-inverse
  `,
  variants: {
    size: {
      sm: 'h-extrasmall w-extrasmall',
      md: 'h-extrasmall w-extrasmall',
      ml: 'h-small w-small',
    },
    disabled: {
      true: 'cursor-not-allowed bg-text-neutral-disabled',
    },
  },
})
